/* eslint-disable camelcase */
import { showErrorAlert, showSuccessAlert } from 'actions';
import {
  evaluateTestSentenceFailure,
  evaluateTestSentenceRequest,
  evaluateTestSentenceSuccess,
  fetchAllCommunicationDocument,
  fetchAllCommunicationDocumentRequest,
  fetchAllCommunicationDocumentSuccess,
  fetchAttachments,
  fetchAttachmentsFailure,
  fetchAttachmentsFulfill,
  fetchAttachmentsRequest,
  fetchAttachmentsSuccess,
  fetchCommunicationDocument,
  fetchCommunicationDocumentFailure,
  fetchCommunicationDocumentRequest,
  fetchCommunicationDocumentSuccess,
  fetchCommunicationSummary,
  fetchCommunicationSummaryFailure,
  fetchCommunicationSummaryRequest,
  fetchCommunicationSummarySuccess,
  generateCommunication,
  generateCommunicationAsync,
  generateCommunicationFailure,
  generateCommunicationRequest,
  generateCommunicationSuccess,
  postEvaluateTestSentence,
  postTestSentence,
  postTestSentenceV2,
  previewFeedback,
  testAnnotator,
  testSentenceFailure,
  testSentenceRequest,
  testSentenceSuccess,
} from 'actions/communication';
import { previewRuleGroupSuccess } from 'actions/ruleGroup';
import { apiClient as LitLingoClient, apiClientV2 as LitLingoClientV2 } from 'client';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getCommunications } from 'selectors/communications';
import type { API, APIV2, SagaReturn } from 'types';

function* postTestSentenceSaga(action: ReturnType<typeof postTestSentence>): SagaReturn {
  const { payload } = action;
  const { body, rules, campaigns, annotators, platforms } = payload;

  yield put(testSentenceRequest());
  const response = (yield call([LitLingoClient.resources.communications.extras, 'preview'], {
    data: {
      body,
      to: [{ type: 'channel', name: 'engineering', id: 'engineering' }],
      type: 'slack',
      platforms: platforms.map((p) => p.toLowerCase()),
      rules,
      campaigns,
      annotators,
    },
  })) as API.Response<API.Communications.Preview>;

  if (response.error != null) {
    yield put(testSentenceFailure(response.error));
    yield put(showErrorAlert(response.error.message || 'Preview failed'));
  } else {
    yield put(testSentenceSuccess(response.data));
  }
}

function* postTestSentenceV2Saga(action: ReturnType<typeof postTestSentenceV2>): SagaReturn {
  const { payload } = action;
  const {
    body,
    platform,
    identifiers,
    models,
    subject,
    inRuleGroup = false,
    force_v2_graphs = false,
    customer_uuid,
  } = payload;

  yield put(testSentenceRequest());
  const data: Record<string, unknown> = {
    body,
    to: [{ type: 'channel', name: 'engineering', id: 'engineering' }],
    type: 'slack',
    platform,
    force_v2_graphs,
    customer_uuid,
  };
  if (subject) {
    data.subject = subject;
  }

  if (identifiers) {
    data.identifiers = identifiers;
  }
  if (models) {
    data.models = models;
  }
  const response = (yield call([LitLingoClientV2.resources.communications.extras, 'preview'], {
    data,
  })) as API.Response<API.Communications.Preview>;

  if (response.error != null) {
    yield put(testSentenceFailure(response.error));
    yield put(showErrorAlert(response.error.message || 'Preview failed'));
  } else if (inRuleGroup) {
    yield put(previewRuleGroupSuccess(response.data));
  } else {
    yield put(testSentenceSuccess(response.data));
  }
}

function* fetchCommunicationSummarySaga(
  action: ReturnType<typeof fetchCommunicationSummary>
): SagaReturn {
  const { payload } = action;
  const { commId } = payload;

  yield put(fetchCommunicationSummaryRequest());

  const response = (yield call([LitLingoClient.resources.communications.extras, 'body'], {
    urlParams: { commUuid: commId },
  })) as API.Response<API.Communications.Body>;
  if (response.error != null) {
    yield put(fetchCommunicationSummaryFailure(response.error));
  } else {
    yield put(fetchCommunicationSummarySuccess({ ...response.data, uuid: commId }));
  }
}

function* postEvaluateTestSentenceSaga(
  action: ReturnType<typeof postEvaluateTestSentence>
): SagaReturn {
  const { payload } = action;
  const { body, platform } = payload;

  yield put(evaluateTestSentenceRequest());
  const response = (yield call([LitLingoClient.resources.communications.extras, 'evaluate'], {
    data: {
      body,
      to: [{ type: 'channel', name: 'engineering', id: 'engineering' }],
      type: 'slack',
      platform: platform.toLowerCase(),
    },
  })) as API.Response<API.Communications.Evaluate>;

  if (response.error != null) {
    yield put(evaluateTestSentenceFailure(response.error));
  } else {
    yield put(evaluateTestSentenceSuccess());
  }
}

function* generateCommunicationSaga(action: ReturnType<typeof generateCommunication>): SagaReturn {
  const { payload } = action;
  const { body, subject, platform, integration, sender, recipients } = payload;

  yield put(generateCommunicationRequest());
  const response = (yield call([LitLingoClient.resources.communications.extras, 'evaluate'], {
    data: {
      body,
      subject,
      type: integration,
      platform,
      sender,
      to: recipients?.map((r) => ({ id: r, name: r, email: r })),
    },
  })) as API.Response<API.Communications.Evaluate>;

  if (response.error != null) {
    yield put(showErrorAlert("Couldn't generate communication."));
    yield put(generateCommunicationFailure(response.error));
  } else {
    yield put(showSuccessAlert('Communication successfully generated.'));
    yield put(generateCommunicationSuccess());
  }
}

function* generateCommunicationAsyncSaga(
  action: ReturnType<typeof generateCommunication>
): SagaReturn {
  const { payload } = action;
  const { body, subject, platform, integration, sender, recipients } = payload;

  yield put(generateCommunicationRequest());
  const response = (yield call([LitLingoClient.resources.communications.extras, 'evaluateAsync'], {
    data: {
      body,
      subject,
      type: 'email',
      integration_type: integration,
      platform,
      sender,
      to: recipients?.map((r) => ({ id: r, name: r, email: r })),
    },
  })) as API.Response<API.Communications.EvaluateAsync>;

  if (response.error != null) {
    yield put(showErrorAlert("Couldn't generate communication."));
    yield put(generateCommunicationFailure(response.error));
  } else {
    yield put(showSuccessAlert('Communication will appear on explore page after processing.'));
    yield put(generateCommunicationSuccess());
  }
}

function* testAnnotatorSaga(action: ReturnType<typeof testAnnotator>): SagaReturn {
  const { payload } = action;
  const { annotator, body } = payload;

  yield put(testSentenceRequest());
  const response = (yield call(
    [LitLingoClient.resources.communications.extras, 'previewAnnotations'],
    {
      data: {
        body,
        to: [{ type: 'channel', name: 'engineering', id: 'engineering' }],
        type: 'slack',
        platform: 'slack',
        annotator,
      },
    }
  )) as API.Response<API.Communications.PreviewAnnotations>;

  if (response.error != null) {
    yield put(testSentenceFailure(response.error));
  } else {
    yield put(testSentenceSuccess(response.data));
  }
}

function* fetchAttachmentsSaga(action: ReturnType<typeof fetchAttachments>): SagaReturn {
  const { payload } = action;
  const { commUuid } = payload;

  yield put(fetchAttachmentsRequest);
  const response = (yield call([LitLingoClient.resources.communications.extras, 'attachments'], {
    urlParams: { commUuid },
  })) as API.Response<API.Communications.Attachments>;

  if (response.error != null) {
    yield put(fetchAttachmentsFailure(response.error));
  } else {
    yield put(fetchAttachmentsSuccess(response.data));
  }
  yield put(fetchAttachmentsFulfill);
}

export function* fetchCommunicationDocumentSaga(
  action: ReturnType<typeof fetchCommunicationDocument>
): SagaReturn {
  const { communication } = action.payload;
  yield put(fetchCommunicationDocumentRequest());
  const response = (yield call([LitLingoClient.resources.communications.extras, 'document'], {
    urlParams: { commUuid: communication.uuid },
  })) as API.Response<API.Communications.Document>;

  if (response.error != null) {
    yield put(fetchCommunicationDocumentFailure(response.error));
  } else {
    yield put(fetchCommunicationDocumentSuccess({ communication, document: response.data }));
  }
}

export function* fetchAllCommunicationDocumentSaga({
  payload,
}: ReturnType<typeof fetchAllCommunicationDocument>): SagaReturn {
  const { communications } = payload;

  const existingCommunications = (yield select(getCommunications)) as ReturnType<
    typeof getCommunications
  >;

  yield put(fetchAllCommunicationDocumentRequest());

  yield all(
    communications
      .filter(
        (c) => !existingCommunications.some((comm) => comm?.uuid === c.uuid && comm?.document)
      )
      .map((c) =>
        call(fetchCommunicationDocumentSaga, {
          payload: {
            communication: c,
          },
          type: fetchCommunicationDocument.toString(),
        })
      )
  );

  yield put(fetchAllCommunicationDocumentSuccess());
}

export function* previewFeedbackSaga(action: ReturnType<typeof previewFeedback>): SagaReturn {
  yield put(fetchCommunicationDocumentRequest());

  const response = (yield call(
    [LitLingoClientV2.resources.communications.extras, 'previewFeedback'],
    {
      data: action.payload,
    }
  )) as API.Response<APIV2.Communications.PreviewFeedback>;

  if (response.error != null) {
    yield put(showErrorAlert('Something went wrong'));
  }
}

// export function* handlePreviewFeedbackSaga({
//   payload,
// }: ReturnType<typeof previewFeedback>): SagaReturn {
//   /* const response = (yield call([LitLingoClient.resources.communications.extras, 'feedback'] )) as API.Response<API.Communications.Feedback>; */
// }

export default function* communicationSaga(): SagaReturn {
  yield takeLatest(postTestSentence.toString(), postTestSentenceSaga);
  yield takeLatest(postTestSentenceV2.toString(), postTestSentenceV2Saga);
  yield takeLatest(postEvaluateTestSentence.toString(), postEvaluateTestSentenceSaga);
  yield takeLatest(testAnnotator.toString(), testAnnotatorSaga);
  yield takeEvery(fetchCommunicationSummary.toString(), fetchCommunicationSummarySaga);
  yield takeLatest(fetchAttachments.toString(), fetchAttachmentsSaga);
  yield takeLatest(fetchCommunicationDocument.toString(), fetchCommunicationDocumentSaga);
  yield takeLatest(fetchAllCommunicationDocument.toString(), fetchAllCommunicationDocumentSaga);
  yield takeLatest(previewFeedback.toString(), previewFeedbackSaga);
  yield takeLatest(generateCommunication.toString(), generateCommunicationSaga);
  yield takeLatest(generateCommunicationAsync.toString(), generateCommunicationAsyncSaga);
  // yield takeLatest(previewFeedback.toString(), handlePreviewFeedbackSaga);
}
